// Catalog.ID Client — Authenticator Key flows (DOM-free)
// ============================================================================
// registration-restructure §15.6, M23. Every decision the Authenticator Key's
// views make lives here: what counts as a code, what a refusal reads like to a
// member, what the Settings status line says, and the four round-trips
// (enable / disable / reveal / issue) plus the login gate's second leg.
//
// The views (login-view, the three wizards, the Settings pane) own the DOM and
// nothing else — the project's pattern since M13's `claim-verification-steps`.
//
// The auth asymmetry mirrors the server's (§15.8) and is visible here:
// `enable` sends a session and a CODE, `disable` a code AND a step-up,
// `reveal`/`issue` a step-up. Do not "simplify" it.

import * as accountApi from '../api/account';
import type { LoginResponse, TotpRevealResponse } from '../api/types';
import { signStepUp } from './step-up';
import { base32Decode, base32Encode, generateTotpSecret, TOTP_DIGITS } from '../crypto/totp';
import { ApiError, isOriginOnlyError } from '../api/client';

// ── the code a member types ─────────────────────────────────────────────────

/** Digits only, at most six. Members paste "123 456" off a phone screen and
 *  some apps show the code with a space in the middle. */
export function normalizeAuthenticatorCode(input: string): string {
    return input.replace(/\D+/g, '').slice(0, TOTP_DIGITS);
}

/** Six digits — the auto-submit trigger: a member never presses a button for a
 *  code they just finished typing. */
export function isCompleteAuthenticatorCode(code: string): boolean {
    return new RegExp(`^\\d{${TOTP_DIGITS}}$`).test(code);
}

// Member-facing copy spells the count out; the CHECK is driven by TOTP_DIGITS,
// so the two cannot disagree without this line being edited on purpose.
const INCOMPLETE = 'Enter the six digits your authenticator app is showing.';

function requireCompleteCode(input: string): string {
    const code = normalizeAuthenticatorCode(input);
    if (!isCompleteAuthenticatorCode(code)) throw new Error(INCOMPLETE);
    return code;
}

// ── failures, in the member's words ─────────────────────────────────────────

/**
 * One line for a member. The per-account 429 is called out as such — it is the
 * one refusal where retrying immediately is useless and the member needs to
 * know why — and every code refusal is the SAME line, because the node
 * deliberately does not say whether a code was wrong, replayed or out of
 * window.
 */
export function describeAuthenticatorError(err: unknown): string {
    const origin = isOriginOnlyError(err);
    if (origin) {
        return `This account is registered on another node. Continue at ${origin.redirectTo}.`;
    }
    if (err instanceof ApiError) {
        if (err.status === 429) {
            const body = err.body as { retryAfterSeconds?: unknown } | null;
            const seconds = typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null;
            const wait = seconds === null ? '' : ` Try again in ${describeWait(seconds)}.`;
            return `Too many authenticator attempts for this account.${wait}`;
        }
        if (err.status === 400 || err.status === 401) {
            return 'That code is not right. Check the app and try the next one.';
        }
    }
    return err instanceof Error ? err.message : 'Something went wrong — try again.';
}

function describeWait(seconds: number): string {
    if (seconds < 60) return `${Math.max(1, Math.ceil(seconds))} seconds`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

// ── Settings status ─────────────────────────────────────────────────────────

/** The Authenticator group's one-line status. */
export function authenticatorStatusLine(state: { enabled: boolean; confirmedAt?: string | null }): string {
    if (!state.enabled) {
        return 'Off. Your password alone signs you in.';
    }
    if (!state.confirmedAt) return 'On.';
    const when = new Date(state.confirmedAt);
    if (Number.isNaN(when.getTime())) return 'On.';
    return `On since ${when.toLocaleDateString()}.`;
}

// ── the round-trips ─────────────────────────────────────────────────────────

/** Everything these flows touch, injected so the logic is testable without a
 *  session, a key bag or a network. */
export interface AuthenticatorDeps {
    signStepUp(purpose: 'totp-manage'): Promise<string>;
    putTotpKey(req: { secret: string; challengeSignature: string }): Promise<void>;
    enableTotp(code: string): Promise<{ enabled: boolean }>;
    disableTotp(req: { code: string; challengeSignature: string }): Promise<{ enabled: boolean }>;
    revealTotp(req: { challengeSignature: string }): Promise<TotpRevealResponse>;
    /** 20 CSPRNG bytes. Injected so a test can pin what gets PUT. */
    generateSecret(): Uint8Array;
}

export const defaultAuthenticatorDeps: AuthenticatorDeps = {
    signStepUp: (purpose) => signStepUp(purpose),
    putTotpKey: (req) => accountApi.putTotpKey(req),
    enableTotp: (code) => accountApi.enableTotp(code),
    disableTotp: (req) => accountApi.disableTotp(req),
    revealTotp: (req) => accountApi.revealTotp(req),
    generateSecret: () => generateTotpSecret(),
};

/** Turn the login gate ON. Session + code only — the code is the proof. */
export async function enableAuthenticator(
    code: string, deps: AuthenticatorDeps = defaultAuthenticatorDeps,
): Promise<void> {
    await deps.enableTotp(requireCompleteCode(code));
}

/** Turn the login gate OFF: a live code AND a step-up. The code is checked
 *  first for shape so a mistyped digit does not burn a single-use challenge. */
export async function disableAuthenticator(
    code: string, deps: AuthenticatorDeps = defaultAuthenticatorDeps,
): Promise<void> {
    const normalized = requireCompleteCode(code);
    const challengeSignature = await deps.signStepUp('totp-manage');
    await deps.disableTotp({ code: normalized, challengeSignature });
}

export interface RevealedAuthenticatorKey {
    secret: Uint8Array;
    /** The `otpauth://` URI, as the node built it — the QR's payload. */
    uri: string;
    enabled: boolean;
    issuedAt: string;
}

/** Fetch the stored key for a new phone, or to print a replacement sheet. */
export async function revealAuthenticator(
    deps: AuthenticatorDeps = defaultAuthenticatorDeps,
): Promise<RevealedAuthenticatorKey> {
    const challengeSignature = await deps.signStepUp('totp-manage');
    const resp = await deps.revealTotp({ challengeSignature });
    return {
        secret: base32Decode(resp.secret),
        uri: resp.uri,
        enabled: resp.enabled,
        issuedAt: resp.issuedAt,
    };
}

/**
 * Mint a NEW key and install it. The old sheet is void from the moment this
 * returns and the login gate is off until a code confirms the new key — the
 * caller must say both of those things before offering the button.
 */
export async function issueAuthenticatorKey(
    deps: AuthenticatorDeps = defaultAuthenticatorDeps,
): Promise<Uint8Array> {
    const secret = deps.generateSecret();
    const challengeSignature = await deps.signStepUp('totp-manage');
    await deps.putTotpKey({ secret: base32Encode(secret), challengeSignature });
    return secret;
}

// ── the login gate's second leg ─────────────────────────────────────────────

export interface TotpLoginDeps {
    loginTotp(req: { cid: string; preAuthToken: string; code: string }): Promise<LoginResponse>;
}

export const defaultTotpLoginDeps: TotpLoginDeps = {
    loginTotp: (req) => accountApi.loginTotp(req),
};

/** Exchange the pre-auth token plus a live code for the ordinary login
 *  response. An incomplete code never reaches the node — it would count against
 *  the per-account limit for nothing. */
export async function submitTotpLogin(
    req: { cid: string; preAuthToken: string; code: string },
    deps: TotpLoginDeps = defaultTotpLoginDeps,
): Promise<LoginResponse> {
    const code = requireCompleteCode(req.code);
    return deps.loginTotp({ cid: req.cid, preAuthToken: req.preAuthToken, code });
}
